import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

export const LiftCrmClientContext = createContext(null);

const validStates = new Set(["missing", "record", "conversation", "ambiguous"]);
const validSourceRoots = new Set(["arflow_clients", "leads", "arflow_prospeccao", "arflow_indicacoes"]);
const idIsKnown = value => value !== undefined && value !== null && value !== ""
  && Number.isInteger(Number(value)) && Number(value) > 0;

function ownerIdFor(draft, sale) {
  const candidates = [
    draft?.consultantId,
    draft?.responsavelId,
    draft?.ownerId,
    draft?.consultorId,
    draft?.responsavel?.id,
    draft?.vendidoPor?.consultantId,
    sale?.vendidoPor?.consultantId,
    sale?.responsavelId,
  ];
  return candidates.find(idIsKnown);
}

function errorMessage(error) {
  return error instanceof Error ? error.message : String(error || "Erro desconhecido");
}

function canonicalSourcePhone(value) {
  let digits = String(value || "").replace(/\D/g, "");
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith("55")) digits = digits.slice(2);
  if (digits.length === 10) digits = `${digits.slice(0, 2)}9${digits.slice(2)}`;
  return /^\d{11}$/.test(digits) ? digits : null;
}

function sourceIdentity(sourceLink) {
  const storeId = sourceLink?.storeId == null ? "" : String(sourceLink.storeId);
  return JSON.stringify([
    String(sourceLink?.rootName || ""),
    String(sourceLink?.key ?? ""),
    storeId,
    canonicalSourcePhone(sourceLink?.phone ?? sourceLink?.telefone),
  ]);
}

function sourceLinkFrom(result) {
  return result?.sourceLink || result?.client?.sourceLink || result?.record?.sourceLink || null;
}

function isValidSourceLink(sourceLink) {
  return Boolean(sourceLink && typeof sourceLink === "object" && !Array.isArray(sourceLink)
    && validSourceRoots.has(sourceLink.rootName)
    && sourceLink.key !== undefined && sourceLink.key !== null && String(sourceLink.key) !== ""
    && Number.isFinite(Number(sourceLink.storeId)) && Number(sourceLink.storeId) > 0);
}

function isOriginBindingPending(result) {
  return result?.state === "ambiguous"
    && result?.reason === "origin_binding_pending"
    && result?.bindingStatus === "pending";
}

function noticeInspectionKey(inspection) {
  if (!inspection || typeof inspection !== "object") return "";
  return JSON.stringify([
    inspection.storeId == null ? "" : String(inspection.storeId),
    String(inspection.pedidoId ?? ""),
    String(inspection.state || ""),
    String(inspection.sourceKind || ""),
    sourceIdentity(inspection.sourceLink),
    String(inspection.originCliente || ""),
    String(inspection.originLabel || ""),
    Boolean(inspection.originNeedsReview),
  ]);
}

function reconcileNoticeInspection(noticeInspection, lookup, storeId, pedido) {
  if (!noticeInspection) return { ok:true, data:lookup };
  if (Number(noticeInspection.storeId) !== Number(storeId)
    || String(noticeInspection.pedidoId) !== String(pedido)) return { ok:false };

  const expectedState = noticeInspection.state === "not_found" ? "missing" : noticeInspection.state;
  if (expectedState === "record") {
    const expectedLink = noticeInspection.sourceLink;
    const currentLink = sourceLinkFrom(lookup);
    if (lookup?.state !== "record"
      || !isValidSourceLink(expectedLink)
      || !isValidSourceLink(currentLink)
      || Number(expectedLink.storeId) !== Number(storeId)
      || Number(currentLink.storeId) !== Number(storeId)
      || sourceIdentity(expectedLink) !== sourceIdentity(currentLink)) return { ok:false };
    return { ok:true, data:lookup };
  }
  if (expectedState === "missing") {
    if (lookup?.state === "missing") return { ok:true, data:lookup };
    if (lookup?.state === "record") {
      const currentLink = sourceLinkFrom(lookup);
      if (isValidSourceLink(currentLink) && Number(currentLink.storeId) === Number(storeId)) {
        return { ok:true, data:lookup };
      }
      return { ok:false };
    }
    if (lookup?.state === "conversation" || lookup?.state === "ambiguous") {
      return { ok:true, data:lookup };
    }
    return { ok:false };
  }
  if (expectedState === lookup?.state
    && ["conversation", "ambiguous"].includes(expectedState)) return { ok:true, data:lookup };
  return { ok:false };
}

export default function LiftCrmClientAction({ storeId, pedido, sale, theme, noticeInspection = null }) {
  const context = useContext(LiftCrmClientContext);
  const hasProvider = context !== null;
  const onCadastrarCliente = context?.onCadastrarCliente;
  const onOpenCrmSource = context?.onOpenCrmSource;
  const revision = context?.revision;
  const canBindOrigin = context?.canBindOrigin !== false;
  const actorIdentity = context?.identity == null ? "" : String(context.identity);
  const actorScopeIdentity = JSON.stringify([actorIdentity, canBindOrigin]);
  const noticeSignature = noticeInspectionKey(noticeInspection);
  const noticeInspectionRef = useRef(noticeInspection);
  noticeInspectionRef.current = noticeInspection;
  const key = storeId != null && storeId !== "" && pedido != null && pedido !== ""
    ? `${String(storeId)}\u0000${String(pedido)}` : "";
  const endpoint = key
    ? `/api/lift/pedidos/${encodeURIComponent(String(storeId))}/${encodeURIComponent(String(pedido))}/atendimento-crm`
    : "";
  const originBindingEndpoint = key
    ? `/api/lift/pedidos/${encodeURIComponent(String(storeId))}/${encodeURIComponent(String(pedido))}/vincular-origem`
    : "";
  const [revisionRequest, setRevisionRequest] = useState(0);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const requestRef = useRef(null);
  const requestIdRef = useRef(0);
  const originBoundKeysRef = useRef(new Set());
  const previousActorIdentityRef = useRef(actorScopeIdentity);
  const actorGenerationRef = useRef(0);
  if (previousActorIdentityRef.current !== actorScopeIdentity) {
    previousActorIdentityRef.current = actorScopeIdentity;
    actorGenerationRef.current += 1;
    originBoundKeysRef.current.clear();
  }
  const actorScope = JSON.stringify([actorIdentity, actorGenerationRef.current, canBindOrigin]);
  const statusScope = JSON.stringify([actorScope, noticeSignature]);
  const activeKeyRef = useRef(key);
  const activeStatusScopeRef = useRef(JSON.stringify([actorScope, noticeSignature]));
  const callbackRef = useRef(onCadastrarCliente);
  const originLinkedCallbackRef = useRef(context?.onOriginLinked);
  activeKeyRef.current = key;
  activeStatusScopeRef.current = JSON.stringify([actorScope, noticeSignature]);
  callbackRef.current = onCadastrarCliente;
  originLinkedCallbackRef.current = context?.onOriginLinked;

  const carregar = useCallback(() => {
    if (!hasProvider || !endpoint) return undefined;
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    const requestId = ++requestIdRef.current;
    const requestScope = JSON.stringify([actorScope, noticeSignature]);
    const isCurrent = () => !controller.signal.aborted
      && requestIdRef.current === requestId && activeKeyRef.current === key
      && activeStatusScopeRef.current === requestScope;
    setStatus({ key, scope:requestScope, loading: true });
    fetch(endpoint, {
      method: "GET",
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    }).then(async response => {
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || `Não foi possível verificar o atendimento no CRM (HTTP ${response.status})`);
      }
      if (!body || !validStates.has(body.state)) {
        throw new Error("Resposta inválida ao verificar o atendimento no CRM.");
      }
      if (!isCurrent()) return;
      const reconciled = reconcileNoticeInspection(noticeInspectionRef.current, body, storeId, pedido);
      if (!reconciled.ok) {
        setStatus({ key, scope:requestScope, loading:false, data:{ state:"unknown" }, error:"" });
        return;
      }
      const inspectedBody = reconciled.data;
      const lookupSourceLink = sourceLinkFrom(inspectedBody);
      const bindKey = JSON.stringify([actorScope, key, sourceIdentity(lookupSourceLink)]);
      const pendingBinding = isOriginBindingPending(inspectedBody);
      const shouldBind = isValidSourceLink(lookupSourceLink)
        && (pendingBinding || (inspectedBody.state === "record" && !originBoundKeysRef.current.has(bindKey)));
      if (canBindOrigin && shouldBind) {
        let bindingBody;
        try {
          const bindingResponse = await fetch(originBindingEndpoint, {
            method: "POST",
            credentials: "same-origin",
            cache: "no-store",
            headers: { Accept: "application/json" },
            signal: controller.signal,
          });
          bindingBody = await bindingResponse.json().catch(() => ({}));
          if (!bindingResponse.ok) {
            throw new Error(bindingBody.error || bindingBody.reason
              || `Não foi possível vincular a origem do cliente (HTTP ${bindingResponse.status})`);
          }
          if (!bindingBody || (bindingBody.state && bindingBody.state !== "record")
            || !isValidSourceLink(sourceLinkFrom(bindingBody))) {
            throw new Error(bindingBody.error || bindingBody.reason || "O vínculo da origem não foi confirmado pelo servidor.");
          }
        } catch (error) {
          if (isCurrent()) {
            setStatus({ key, scope:requestScope, loading: false, data: inspectedBody, bindingError: errorMessage(error) });
          }
          return;
        }
        if (!isCurrent()) return;
        originBoundKeysRef.current.add(bindKey);
        setStatus({ key, scope:requestScope, loading: false, data: { ...bindingBody, state: "record" }, error: "" });
        if (bindingBody.sale) originLinkedCallbackRef.current?.(bindingBody.sale);
        return;
      }
      setStatus({ key, scope:requestScope, loading: false, data: inspectedBody, error: "" });
    }).catch(error => {
      if (isCurrent()) setStatus({ key, scope:requestScope, loading: false, error: errorMessage(error) });
    }).finally(() => {
      if (requestRef.current === controller) requestRef.current = null;
    });
    return controller;
  }, [actorScope, canBindOrigin, endpoint, hasProvider, key, noticeSignature, originBindingEndpoint, pedido, storeId]);

  useEffect(() => {
    setSaving(false);
    setSaveError("");
  }, [statusScope]);

  useEffect(() => {
    if (!hasProvider) {
      requestRef.current?.abort();
      requestRef.current = null;
      setStatus(null);
      return undefined;
    }
    const controller = carregar();
    return () => {
      requestIdRef.current += 1;
      controller?.abort();
      if (requestRef.current === controller) requestRef.current = null;
    };
  }, [actorScope, carregar, hasProvider, revision, revisionRequest]);

  if (!hasProvider || !key) return null;
  const currentStatus = status?.key === key && status?.scope === statusScope ? status : null;
  if (!currentStatus || currentStatus.loading) return null;
  if (currentStatus.error) {
    return <div role="alert" style={{ marginTop: 6, fontSize: 12, color: theme?.error || "#991B1B" }}>
      Não foi possível verificar o atendimento no CRM: {currentStatus.error}
    </div>;
  }
  const line = theme?.nuLine || theme?.sandDark || "#CBD5E1";
  const muted = theme?.muted || "#64748B";
  const accent = theme?.accent || "#183049";
  const recordData = currentStatus.data;
  if (recordData?.state === "unknown") {
    return <div role="status" data-lift-crm-lookup-mismatch="" style={{ marginTop: 7, color: "#92400E", fontSize: 12 }}>
      O resultado do CRM mudou desde a verificação do aviso. Atualize a tela para confirmar o cadastro antes de continuar.
    </div>;
  }
  const pendingOriginBinding = isOriginBindingPending(recordData);
  const retryOriginBinding = event => {
    event.preventDefault();
    event.stopPropagation();
    setStatus({ key, scope:statusScope, loading: true });
    setRevisionRequest(value => value + 1);
  };
  const retryOriginBindingButton = <button
    type="button"
    onClick={retryOriginBinding}
    onKeyDown={event => event.stopPropagation()}
    onKeyUp={event => event.stopPropagation()}
    style={{ padding: "4px 8px", border: `1px solid ${line}`, borderRadius: 4, background: "#fff", color: accent, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
  >Tentar vincular origem</button>;
  if (currentStatus.bindingError || pendingOriginBinding) {
    if (!canBindOrigin) {
      return <div role="status" style={{ marginTop: 7, color: muted, fontSize: 12 }}>
        A origem do atendimento está pendente de confirmação no CRM.
      </div>;
    }
    if (pendingOriginBinding && !isValidSourceLink(sourceLinkFrom(recordData))) {
      return <div role="alert" style={{ marginTop: 7, color: theme?.error || "#991B1B", fontSize: 12 }}>
        Não foi possível validar o vínculo pendente da origem. Nenhuma conversa foi aberta nem alteração foi enviada.
      </div>;
    }
    return <div data-lift-crm-origin-binding-error="" role={currentStatus.bindingError ? "alert" : "status"} style={{ marginTop: 7, display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap", color: currentStatus.bindingError ? theme?.error || "#991B1B" : muted, fontSize: 12 }}>
      {currentStatus.bindingError
        ? <>Não foi possível confirmar o vínculo da origem: {currentStatus.bindingError}</>
        : "Vínculo da origem pendente de confirmação no CRM."}
      {retryOriginBindingButton}
    </div>;
  }
  if (recordData?.state === "record" || recordData?.state === "conversation") {
    const client = recordData.client || recordData.record || null;
    const sourceKind = recordData.sourceKind || client?.sourceKind;
    const originCliente = recordData.originCliente || client?.originCliente;
    const sourceLink = recordData.sourceLink || client?.sourceLink || null;
    const originReviewLabel = recordData.originReviewLabel || client?.originReviewLabel;
    const sourceLabels = {
      lead: "Lead · Anúncio",
      client: "Presencial",
      prospect: "Prospecção",
      indicacao: "Indicação",
    };
    const allowedOriginLabels = new Set(["Lead · Anúncio", "Presencial", "Prospecção", "Indicação"]);
    const backendOriginLabel = recordData.originLabel || client?.originLabel;
    const normalizedOrigin = String(originCliente || "").normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR").replace(/[^a-z0-9]/g, "");
    const originClienteLabels = {
      lead: "Lead · Anúncio",
      leadanuncio: "Lead · Anúncio",
      porta: "Presencial",
      portapresencial: "Presencial",
      presencial: "Presencial",
      atendimentopresencial: "Presencial",
      prospect: "Prospecção",
      prospeccao: "Prospecção",
      indicacao: "Indicação",
    };
    const originClienteLabel = Object.prototype.hasOwnProperty.call(originClienteLabels, normalizedOrigin)
      ? originClienteLabels[normalizedOrigin] : "";
    const originLabel = (allowedOriginLabels.has(backendOriginLabel) && backendOriginLabel)
      || originClienteLabel
      || (Object.prototype.hasOwnProperty.call(sourceLabels, sourceKind) ? sourceLabels[sourceKind] : "");
    const sourceStoreId = client?.storeId ?? recordData.storeId ?? sourceLink?.storeId ?? storeId;
    const sourcePhone = client?.phone ?? client?.telefone ?? recordData.phone ?? recordData.telefone
      ?? sourceLink?.phone ?? sourceLink?.telefone ?? "";
    const badgeStyle = {
      display: "inline-block",
      border: `1px solid ${line}`,
      borderRadius: 4,
      background: "#F8FAFC",
      color: accent,
      padding: "3px 7px",
      fontSize: 12,
      fontWeight: 700,
    };
    return <div data-lift-crm-client-origin="" style={{ marginTop: 7, display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
      {originLabel && <span style={badgeStyle}>{originLabel}</span>}
      {originReviewLabel && <span role="status" style={{ ...badgeStyle, borderColor: "#FCD34D", background: "#FFFBEB", color: "#92400E" }}>{originReviewLabel}</span>}
      {sourceLink && typeof onOpenCrmSource === "function" && <button
        type="button"
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          onOpenCrmSource(sourceLink, { sourceKind, storeId: sourceStoreId, phone: sourcePhone, client });
        }}
        onKeyDown={event => event.stopPropagation()}
        onKeyUp={event => event.stopPropagation()}
        style={{ padding: "4px 8px", border: `1px solid ${line}`, borderRadius: 4, background: "#fff", color: accent, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
      >Abrir conversa</button>}
    </div>;
  }
  if (recordData?.state === "ambiguous") {
    return <div role="alert" style={{ marginTop: 7, color: theme?.error || "#991B1B", fontSize: 12 }}>
      Não foi possível confirmar a origem deste atendimento no CRM. Nenhuma conversa foi aberta nem alteração foi enviada.
    </div>;
  }
  if (recordData?.state !== "missing") return null;

  const draft = currentStatus.data.draft;
  const latestSale = currentStatus.data.sale || sale || null;
  const ownerKnown = idIsKnown(ownerIdFor(draft, latestSale));
  const draftAvailable = Boolean(draft && typeof draft === "object" && !Array.isArray(draft)
    && Object.keys(draft).length);
  let unavailableReason = "";
  if (!draftAvailable) unavailableReason = "Cadastro indisponível: não foi possível preparar os dados do pedido no LIFT.";
  else if (!ownerKnown) unavailableReason = "Cadastro indisponível: o responsável pelo pedido não foi identificado.";
  else if (typeof onCadastrarCliente !== "function") unavailableReason = "Cadastro indisponível: a ação de cadastro não está configurada.";

  const handleCadastrar = async event => {
    event.preventDefault();
    event.stopPropagation();
    if (saving || unavailableReason) return;
    setSaving(true);
    setSaveError("");
    try {
      const savedClient = await callbackRef.current?.({
        storeId,
        pedido,
        draft,
        sale: latestSale,
      });
      if (activeKeyRef.current !== key || activeStatusScopeRef.current !== statusScope) return;
      if (!savedClient) {
        setSaveError("O cadastro não foi confirmado. Nenhum atendimento do CRM foi alterado.");
        return;
      }
      setStatus({ key, scope:statusScope, loading: true });
      setRevisionRequest(value => value + 1);
    } catch (error) {
      if (activeKeyRef.current === key && activeStatusScopeRef.current === statusScope) setSaveError(errorMessage(error));
    } finally {
      if (activeKeyRef.current === key && activeStatusScopeRef.current === statusScope) setSaving(false);
    }
  };
  const badgeStyle = {
    display: "inline-block",
    border: "1px solid #FECACA",
    borderRadius: 4,
    background: "#FEF2F2",
    color: "#991B1B",
    padding: "3px 7px",
    fontSize: 12,
    fontWeight: 700,
  };
  return <div data-lift-crm-client-action="" style={{ marginTop: 7, display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
    <span style={badgeStyle}>Sem atendimento no CRM</span>
    <button
      type="button"
      disabled={Boolean(unavailableReason) || saving}
      aria-describedby={unavailableReason ? `lift-crm-disabled-${encodeURIComponent(String(storeId))}-${encodeURIComponent(String(pedido))}` : undefined}
      onClick={handleCadastrar}
      onKeyDown={event => event.stopPropagation()}
      onKeyUp={event => event.stopPropagation()}
      style={{ padding: "5px 9px", border: `1px solid ${line}`, borderRadius: 4, background: "#fff", color: accent, fontSize: 12, fontWeight: 700, cursor: unavailableReason || saving ? "not-allowed" : "pointer", opacity: unavailableReason ? 0.65 : 1 }}
    >
      {saving ? "Cadastrando…" : "Cadastrar cliente"}
    </button>
    {unavailableReason && <span id={`lift-crm-disabled-${encodeURIComponent(String(storeId))}-${encodeURIComponent(String(pedido))}`} style={{ flexBasis: "100%", color: muted, fontSize: 12 }}>
      {unavailableReason}
    </span>}
    {saveError && <span role="alert" style={{ flexBasis: "100%", color: theme?.error || "#991B1B", fontSize: 12 }}>{saveError}</span>}
  </div>;
}